import React, {
  createContext,
  useContext,
  useState,
  useEffect,
} from "../react-polyfill";
import Web3 from "web3";
import { toast } from "react-toastify";

// ABI minimal pour le token BBT (standard BEP20/ERC20)
const BEP20_ABI = [
  {
    constant: true,
    inputs: [],
    name: "name",
    outputs: [{ name: "", type: "string" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: true,
    inputs: [],
    name: "symbol",
    outputs: [{ name: "", type: "string" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: true,
    inputs: [],
    name: "decimals",
    outputs: [{ name: "", type: "uint8" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: true,
    inputs: [{ name: "owner", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: false,
    inputs: [
      { name: "to", type: "address" },
      { name: "value", type: "uint256" },
    ],
    name: "transfer",
    outputs: [{ name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    constant: true,
    inputs: [],
    name: "totalSupply",
    outputs: [{ name: "", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: true,
    inputs: [],
    name: "totalBurned",
    outputs: [{ name: "", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
];

// BSC Testnet RPC URL
const BSC_TESTNET_RPC = "https://data-seed-prebsc-1-s1.binance.org:8545/";

// Création du contexte
const Web3Context = createContext();

export const Web3Provider = ({ children }) => {
  const [web3, setWeb3] = useState(null);
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [networkId, setNetworkId] = useState(null);
  const [error, setError] = useState(null);

  // Adresse du contrat BBT sur BSC Testnet
  const BBT_CONTRACT_ADDRESS = "0x56Ca41d5d5Eef8E8f5655b2b8d9Ccb376f189734";

  const connectWallet = async () => {
    try {
      setError(null);

      // Vérifier si MetaMask est installé
      if (window.ethereum) {
        // Créer une instance Web3
        const web3Instance = new Web3(window.ethereum);
        setWeb3(web3Instance);

        // Demander l'autorisation de connexion
        const accounts = await window.ethereum.request({
          method: "eth_requestAccounts",
        });

        setAccount(accounts[0]);

        // Obtenir les informations du réseau
        const chainId = await web3Instance.eth.getChainId();
        const networkId = await web3Instance.eth.net.getId();

        setChainId(chainId);
        setNetworkId(networkId);

        // Configurer les écouteurs d'événements
        window.ethereum.on("accountsChanged", (accounts) => {
          setAccount(accounts[0] || null);
        });

        window.ethereum.on("chainChanged", (chainId) => {
          window.location.reload(); // Recharger la page sur changement de réseau
        });

        return { web3Instance, account: accounts[0] };
      } else {
        setError(
          "MetaMask n'est pas installé. Veuillez l'installer pour utiliser cette fonctionnalité.",
        );
        return null;
      }
    } catch (error) {
      console.error("Erreur lors de la connexion au wallet:", error);
      setError(error.message || "Erreur de connexion au wallet");
      return null;
    }
  };

  const value = {
    web3,
    account,
    chainId,
    networkId,
    error,
    connectWallet,
    isConnected: !!account,
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
};

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error(
      "useWeb3 doit être utilisé à l'intérieur d'un Web3Provider",
    );
  }
  return context;
};

export default Web3Context;
